import { ChevronBig, Plus } from '@portfolio/icons'
import { keyframes, styled } from '@portfolio/styles'
import {
  Root as RadixRoot,
  Item as RadixItem,
  Trigger as RadixTrigger,
  Content as RadixContent,
} from '@radix-ui/react-accordion'
import { ReactNode } from 'react'

interface AccordionProps {
  children: ReactNode
}

function Accordion({ children }: AccordionProps) {
  return (
    <RadixRoot type="single" collapsible>
      {children}
    </RadixRoot>
  )
}

interface AccordionItemProps {
  children: ReactNode
  value: string
  isDivider?: boolean
}

function AccordionItem({
  children,
  value,
  isDivider = true,
}: AccordionItemProps) {
  return (
    <Item value={value} isDivider={isDivider}>
      {children}
    </Item>
  )
}

const Item = styled(RadixItem, {
  variants: {
    isDivider: {
      true: {
        borderTop: '1px solid #2D2D2D',

        '&:last-child': {
          borderBottom: '1px solid #2D2D2D',
        },
      },
    },
  },
})

type AccordionIconVariant = 'chevron' | 'plus'

interface AccordionTriggerProps {
  children: ReactNode
  icon?: AccordionIconVariant
}

function AccordionTriggerIcon({ icon }: { icon: AccordionIconVariant }) {
  switch (icon) {
    case 'chevron':
      return <ChevronBig />
    case 'plus':
      return <Plus />
  }
}

function AccordionTrigger({ children, icon = 'plus' }: AccordionTriggerProps) {
  return (
    <Trigger icon={icon}>
      {children}
      <AccordionTriggerIcon icon={icon} />
    </Trigger>
  )
}

const Trigger = styled(RadixTrigger, {
  display: 'flex',
  gap: 24,
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%',
  paddingBlock: 24,
  projectFont: 'heading07',
  color: '$white',
  textAlign: 'left',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',

  ' svg': {
    flexShrink: 0,
    transition: 'transform 0.3s ease-out',
  },

  '&:focus-visible': {
    outline: '$focusBlue solid 3px;',
  },

  variants: {
    icon: {
      chevron: {
        '& svg': {
          transform: 'rotate(90deg)',
        },

        '&[data-state="open"] svg': {
          transform: 'rotate(-90deg)',
        },
      },
      plus: {
        // Target both vertical paths
        '& svg path:nth-child(1), & svg path:nth-child(2)': {
          transformOrigin: 'center center',
          transition: 'transform 0.3s ease-out',
          transform: 'scaleY(1)',
        },
        '&[data-state="open"] svg path:nth-child(1), &[data-state="open"] svg path:nth-child(2)':
          {
            transform: 'scaleY(0)',
          },
      },
    },
  },
})

interface AccordionContentProps {
  children: ReactNode
}

function AccordionContent({ children }: AccordionContentProps) {
  return (
    <Content>
      <ContentInnerWrapper>{children}</ContentInnerWrapper>
    </Content>
  )
}

const slideDown = keyframes({
  from: {
    gridTemplateRows: '0fr',
  },
  to: {
    gridTemplateRows: '1fr',
  },
})

const slideUp = keyframes({
  from: {
    gridTemplateRows: '1fr',
  },
  to: {
    gridTemplateRows: '0fr',
  },
})

const Content = styled(RadixContent, {
  display: 'grid',
  projectFont: 'body03',
  color: '$gray300',

  '&[data-state="open"]': {
    animation: `${slideDown} 0.5s cubic-bezier(0, 0, 0, 1.14)`,
  },

  '&[data-state="closed"]': {
    animation: `${slideUp} 0.5s cubic-bezier(0, 0, 0, 1.14)`,
  },
})

const ContentInnerWrapper = styled('div', {
  overflow: 'hidden',
})

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
